import { useCallback, useMemo } from 'react';
import { Dropdown, Tooltip, message } from 'antd';
import { CaretDownOutlined, CheckOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { usePanelNavigate } from '../../hooks/usePanelNavigate';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultPostLoginRoute, isRouteAccessible } from '../../config/panelMenuAccess';
import { canAccessWorkspace } from '../../utils/orgPermissionAccess';
import { canCreatePersonalOrganization } from '../../utils/authAccess';
import EntityCircleBadge from '../common/EntityCircleBadge';
import OrganizationLogoMark from '../common/OrganizationLogoMark';
import {
  organizationAccent,
  organizationDisplayName,
  organizationIsPersonal,
  organizationLogoUrl,
  organizationRoleLabel,
  organizationRoleLabelFa,
  userHasPersonalOrganization,
  workspaceContextLabel,
  workspaceSwitcherCaption,
  workspaceSwitcherRoleCaption,
  workspaceSwitcherTooltip,
} from '../../utils/organization';
import { getApiErrorMessage } from '../../utils/apiError';

const PERSONAL_TAG_CLASS =
  'shrink-0 rounded-md border border-violet-200 bg-violet-50 px-1.5 py-0 text-[10px] leading-4 text-violet-700 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-300';

function WorkspaceItemLabel({ org, isActive, showRole }) {
  const name = organizationDisplayName(org);
  const isPersonal = organizationIsPersonal(org);
  const role = showRole ? organizationRoleLabelFa(organizationRoleLabel(org)) : null;

  return (
    <div className="flex w-full min-w-0 max-w-[17rem] items-center gap-2.5 py-0.5">
      <OrganizationLogoMark
        imageUrl={organizationLogoUrl(org)}
        label={name}
        accentColor={organizationAccent(org.id)}
        width={52}
        height={32}
      />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden text-right">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-sm font-medium text-slate-800 dark:text-slate-100" title={name}>
            {name}
          </span>
          {isPersonal ? <span className={PERSONAL_TAG_CLASS}>فضای شخصی</span> : null}
        </span>
        {role ? (
          <span className="block truncate text-[11px] text-slate-500 dark:text-slate-400">{role}</span>
        ) : null}
      </div>
      {isActive ? <CheckOutlined className="shrink-0 text-brand-500" /> : null}
    </div>
  );
}

function toMenuItem(org, activeOrganization, { showRole }) {
  return {
    key: String(org.id),
    label: (
      <WorkspaceItemLabel
        org={org}
        isActive={activeOrganization && String(activeOrganization.id) === String(org.id)}
        showRole={showRole}
      />
    ),
  };
}

const CREATE_PERSONAL_ITEM = {
  key: '_create_personal_workspace',
  label: <span className="text-sm text-brand-600 dark:text-brand-400">ایجاد فضای کاری شخصی</span>,
};

const EMPTY_ITEM = {
  key: '_empty',
  disabled: true,
  label: <span className="text-xs text-slate-500 dark:text-slate-400">سازمانی یافت نشد.</span>,
};

function WorkspaceTriggerChip({ displayOrg, roleCaption, switchingOrg, showCaret }) {
  const name = displayOrg ? organizationDisplayName(displayOrg) : 'بدون سازمان';

  return (
    <span className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden text-right">
      <span className="flex w-full min-w-0 items-center justify-between gap-2">
        <OrganizationLogoMark
          imageUrl={organizationLogoUrl(displayOrg)}
          label={displayOrg ? name : '؟'}
          accentColor={displayOrg ? organizationAccent(displayOrg.id) : '#94a3b8'}
          width={128}
          height={32}
        />
        {showCaret ? (
          <CaretDownOutlined
            className={classNames(
              'shrink-0 text-[10px] text-slate-400',
              !switchingOrg && 'transition-colors group-hover:text-brand-600',
            )}
          />
        ) : null}
      </span>
      <span
        className="line-clamp-2 w-full min-w-0 break-words text-[13px] font-semibold leading-5 text-slate-900 dark:text-white"
        title={name}
      >
        {name}
      </span>
      {roleCaption ? (
        <span className="block w-full truncate text-[11px] leading-4 text-slate-500 dark:text-slate-400">{roleCaption}</span>
      ) : null}
    </span>
  );
}

/**
 * سوئیچر سازمان فعال / فضای کاری شخصی در هدر سایدبار.
 */
const WorkspaceSwitcher = ({ collapsed = false, isMobile = false, onToggle }) => {
  const navigate = usePanelNavigate();
  const location = useLocation();
  const {
    user,
    organizations,
    activeOrganization,
    switchActiveOrganization,
    switchingOrg,
    impersonationLockedOrganizationId,
  } = useAuth();

  const orgSwitchLocked = Boolean(impersonationLockedOrganizationId);
  const showWorkspaceSettings = canAccessWorkspace(user, organizations, activeOrganization);
  const showCreatePersonalWorkspace =
    canCreatePersonalOrganization(user) && !userHasPersonalOrganization(organizations);
  const displayOrg = activeOrganization || organizations[0];
  const hasOrgs = organizations.length > 0;
  const canSwitch = !orgSwitchLocked && organizations.length > 1;
  const caption = displayOrg
    ? workspaceSwitcherCaption(displayOrg, organizations, { canSwitch })
    : workspaceContextLabel(null);
  const roleCaption = workspaceSwitcherRoleCaption(displayOrg);
  const collapsedTooltip = workspaceSwitcherTooltip(displayOrg, organizations, { canSwitch });

  const handleWorkspaceMenuClick = useCallback(
    async ({ key }) => {
      if (key === '_workspace_settings') {
        navigate(ROUTES.WORKSPACE);
        if (isMobile) onToggle?.();
        return;
      }
      if (key === '_create_personal_workspace') {
        navigate(ROUTES.PERSONAL_WORKSPACE_NEW);
        if (isMobile) onToggle?.();
        return;
      }
      if (!hasOrgs || key === '_empty' || orgSwitchLocked) return;
      if (String(activeOrganization?.id) === String(key)) return;
      try {
        await switchActiveOrganization(String(key));
        message.success('سازمان فعال تغییر کرد');
        if (!isRouteAccessible(location.pathname, { user, activeOrganization: { id: key }, organizations })) {
          navigate(getDefaultPostLoginRoute({ user, activeOrganization: { id: key }, organizations }));
        }
      } catch (e) {
        message.error(getApiErrorMessage(e, 'تغییر سازمان ناموفق بود.'));
      }
    },
    [
      activeOrganization?.id,
      hasOrgs,
      orgSwitchLocked,
      switchActiveOrganization,
      navigate,
      isMobile,
      onToggle,
      location.pathname,
      user,
      organizations,
    ],
  );

  const workspaceMenu = useMemo(() => {
    let orgItems;
    if (!hasOrgs) {
      orgItems = showCreatePersonalWorkspace ? [CREATE_PERSONAL_ITEM] : [EMPTY_ITEM];
    } else {
      const personalOrgs = organizations.filter((org) => organizationIsPersonal(org));
      const companyOrgs = organizations.filter((org) => !organizationIsPersonal(org));
      const mixed = personalOrgs.length > 0 && companyOrgs.length > 0;

      if (mixed) {
        orgItems = [
          {
            type: 'group',
            key: 'personal-group',
            label: 'فضای کاری شخصی',
            children: personalOrgs.map((org) => toMenuItem(org, activeOrganization, { showRole: false })),
          },
          {
            type: 'group',
            key: 'company-group',
            label: 'سازمان‌ها',
            children: companyOrgs.map((org) => toMenuItem(org, activeOrganization, { showRole: true })),
          },
        ];
      } else if (personalOrgs.length > 0) {
        orgItems = personalOrgs.map((org) => toMenuItem(org, activeOrganization, { showRole: false }));
      } else {
        orgItems = companyOrgs.map((org) => toMenuItem(org, activeOrganization, { showRole: true }));
      }
    }

    const items = [...orgItems];
    if (showCreatePersonalWorkspace && hasOrgs) {
      items.push({ type: 'divider' });
      items.push(CREATE_PERSONAL_ITEM);
    }
    if (showWorkspaceSettings) {
      items.push({ type: 'divider' });
      items.push({
        key: '_workspace_settings',
        label: <span className="text-sm text-brand-600 dark:text-brand-400">تنظیمات سازمان</span>,
      });
    }

    return {
      items,
      onClick: handleWorkspaceMenuClick,
    };
  }, [
    organizations,
    activeOrganization,
    hasOrgs,
    handleWorkspaceMenuClick,
    showWorkspaceSettings,
    showCreatePersonalWorkspace,
  ]);

  const triggerClassName = classNames(
    'app-sidebar-workspace',
    'group flex w-full min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-xl border border-slate-200/80 px-2.5 py-2',
    'bg-white/70 text-right backdrop-blur-sm',
    'dark:border-white/10 dark:bg-white/[0.04]',
  );

  if (collapsed) {
    const badge = (
      <EntityCircleBadge
        imageUrl={organizationLogoUrl(displayOrg)}
        label={displayOrg ? organizationDisplayName(displayOrg) : '؟'}
        accentColor={displayOrg ? organizationAccent(displayOrg.id) : '#94a3b8'}
        size={44}
        roundedClassName="rounded-xl"
        objectFit="contain"
      />
    );

    if (orgSwitchLocked) {
      return (
        <div className="relative min-w-0 px-3 pb-4">
          <Tooltip title={collapsedTooltip} placement="left">
            <div className="mx-auto">{badge}</div>
          </Tooltip>
        </div>
      );
    }

    return (
      <div className="relative min-w-0 px-3 pb-4">
        <Tooltip title={collapsedTooltip} placement="left">
          <span className="mx-auto inline-flex">
            <Dropdown
              menu={workspaceMenu}
              trigger={['click']}
              placement="bottomRight"
              arrow
              disabled={switchingOrg}
              classNames={{ root: 'app-sidebar-workspace-menu' }}
              overlayStyle={{ minWidth: 260, maxWidth: 320 }}
            >
              <button
                type="button"
                disabled={switchingOrg}
                aria-label={collapsedTooltip}
                className="transition-all duration-200 hover:scale-105 disabled:pointer-events-none disabled:opacity-70"
              >
                {badge}
              </button>
            </Dropdown>
          </span>
        </Tooltip>
      </div>
    );
  }

  if (orgSwitchLocked) {
    return (
      <div className="relative min-w-0 px-3 pb-4">
        <Tooltip title="در حالت ورود به‌عنوان کاربر دیگر، فقط به همین سازمان دسترسی دارید">
          <div className={triggerClassName}>
            <WorkspaceTriggerChip displayOrg={displayOrg} roleCaption={roleCaption} switchingOrg={false} showCaret={false} />
          </div>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="relative min-w-0 px-3 pb-4">
      <Dropdown
        menu={workspaceMenu}
        trigger={['click']}
        placement="bottomRight"
        arrow
        disabled={switchingOrg}
        classNames={{ root: 'app-sidebar-workspace-menu' }}
        overlayStyle={{ minWidth: 260, maxWidth: 320 }}
      >
        <button
          type="button"
          disabled={switchingOrg}
          aria-label={caption}
          className={classNames(
            triggerClassName,
            'transition-all duration-200',
            'hover:border-brand-200 hover:bg-white hover:shadow-soft',
            'dark:hover:border-brand-500/30 dark:hover:bg-white/[0.07]',
            switchingOrg && 'pointer-events-none opacity-70',
          )}
        >
          <WorkspaceTriggerChip
            displayOrg={displayOrg}
            roleCaption={roleCaption}
            switchingOrg={switchingOrg}
            showCaret
          />
        </button>
      </Dropdown>
    </div>
  );
};

export default WorkspaceSwitcher;
